import React, { useEffect, useState } from 'react';
import { DialogProps, TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from 'tss-react/mui';

import { Modal } from '../../../../components/other/Modals';
import { useAddBalance } from '../hooks/useAddBalance';
import { DriverModal } from '../../orders/orderPage/DriverModal';
import { IUser } from '../../../../interfaces/user';
import { useFormatMessage } from '../../../../hooks';
import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';

export const useStyles = makeStyles()(theme => {
  return {
    companySearch: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      paddingBottom: 0,
    },
    containerAll: {
      marginTop: 8,
      marginLeft: 10,
    },
  };
});

const getInitialValues = (): {
  driver: IUser | null;
  amount: number;
} => ({
  driver: null,
  amount: 0,
});

export function ModaAddBalance({
  showModal,
  closeModal,
  handleSucess,
}: {
  showModal: boolean;
  closeModal: () => void;
  handleSucess?: () => void;
}) {
  const fm = useFormatMessage();
  const { classes } = useStyles();
  const [showDriverModal, setDriverModal] = useState(false);
  const { fetchBalance, loading, success } = useAddBalance();

  useEffect(() => {
    if (success) {
      handleSucess?.();
      closeModal();
    }
  }, [success]);

  const {
    values,
    setFieldValue,
    touched,
    errors,
    handleBlur,
    handleSubmit,
    handleChange,
    resetForm,
  } = useFormik({
    initialValues: getInitialValues(),
    validationSchema: Yup.object().shape({
      amount: Yup.number()
        .required(fm('AUTH.VALIDATION.REQUIRED_FIELD'))
        .min(1, fm('AUTH.VALIDATION.REQUIRED_FIELD')),
      driver: Yup.object()
        .nullable()
        .shape({
          id: Yup.number().nullable().required(fm('AUTH.VALIDATION.REQUIRED_FIELD')),
        })
        .required(fm('AUTH.VALIDATION.REQUIRED_FIELD')),
    }),
    onSubmit: submitValues => {
      if (!submitValues.driver) return;
      fetchBalance(submitValues.driver.id, submitValues.amount);
    },
  });

  useEffect(() => {
    if (!showModal) {
      resetForm();
    }
  }, [showModal]);

  return (
    <>
      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={showModal}
        onClose={closeModal}
        title={fm('ADD.BALANCE')}
        content={
          <>
            <div className={classes.companySearch}>
              <TextField
                type='text'
                label={fm('ORDER.TABLE.DRIVER.INFO')}
                margin='normal'
                value={`${values.driver?.fio ? `${values.driver?.fio},` : ''} ${
                  values.driver?.phone ? `+${values.driver?.phone}` : ''
                }`}
                variant='outlined'
                helperText={touched.driver && errors.driver?.toString()}
                error={Boolean(touched.driver && errors.driver)}
                onBlur={handleBlur}
                onChange={handleChange}
                inputProps={{ readOnly: true }}
              />
              <div className={classes.containerAll}>
                <ButtonWithLoader
                  height={52}
                  onPress={() => {
                    setDriverModal(true);
                  }}
                >
                  {fm('DRIVERS.ALL')}
                </ButtonWithLoader>
              </div>
            </div>
            <TextField
              type='number'
              label={fm('PARTICIPATION.FORM.SUM')}
              margin='normal'
              name='amount'
              value={values.amount}
              variant='outlined'
              helperText={touched.amount && errors.amount?.toString()}
              error={Boolean(touched.amount && errors.amount)}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <ButtonWithLoader disabled={loading} loading={loading} onPress={handleSubmit}>
              {fm('USER.BUTTON.ADD')}
            </ButtonWithLoader>
          </>
        }
        actions={[
          {
            title: fm('COMMON.BUTTON.CLOSE'),
            onClick: () => closeModal(),
          },
        ]}
      />

      <DriverModal
        showDriverModal={showDriverModal}
        closeDriverModal={() => setDriverModal(false)}
        setFieldValue={setFieldValue}
        values={values}
      />
    </>
  );
}
